import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Document : famille "fichier conservé".
 *
 * Fichier (PDF, image, scan) conservé et rattachable à n'importe quelle entité
 * du socle (agreement, consent, credential, inspection, invoice, custody,
 * incident...), avec version, validité et règles de visibilité. Construit sur
 * le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du fichier ("contrat",
 * "carte d'identité", "photo") : elle est portée par le discriminant `type`
 * (chaîne libre déclarée par l'app). La visibilité (`visibility`) est un état
 * métier à part, hérité côté app de l'entité la plus restrictive de `linkedRefs`.
 *
 * Rattachements vers d'autres familles = de simples ids (aucun import d'un
 * autre schéma) : `linkedRefs` porte des couples {collection, id} car un même
 * document peut viser plusieurs entités de familles différentes.
 *
 * Sécurité (cf. fiche) : `fileUrl` ne doit JAMAIS être une URL publique
 * permanente. L'app stocke un chemin de stockage (ou une URL signée de courte
 * durée) et régénère l'accès à la lecture. En v0 la famille reste org-wide
 * (même doctrine que asset) ; l'accès fin par visibilité viendra après.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille document (hors champs standard du socle). */
export const documentCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "contrat", "scan", "photo"). */
  type: z.string().min(1),
  /** Pointeur vers le fichier : chemin de stockage ou URL signée de courte durée, jamais une URL publique permanente (cf. sécurité). Cœur de la famille. */
  fileUrl: z.string().min(1),
  /** Type MIME du fichier (ex. "application/pdf", "image/png"). Optionnel. */
  mimeType: z.string().min(1).optional(),
  /** Taille du fichier en octets. Optionnel. */
  size: z.number().int().optional(),
  /** Entités rattachées : couples {collection, id} vers n'importe quelle famille du socle (agreement, consent, credential, inspection, invoice, custody, incident...). Optionnel. */
  linkedRefs: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Numéro de version / révision du fichier. Optionnel. */
  version: z.number().int().optional(),
  /** Niveau de visibilité, chaîne libre pilotée par l'app (ex. "org", "restreint", "prive"), hérité côté app de l'entité la plus restrictive de `linkedRefs`. Optionnel. */
  visibility: z.string().min(1).optional(),
  /** Date d'expiration de validité du document. Optionnel. */
  expiresAt: z.date().optional(),
} as const;

/** Schéma complet de la famille document (champs standard + champs métier). */
export const DocumentSchema = z.object({
  ...baseEntityFields,
  ...documentCoreFields,
});

/** Type d'un document. */
export type Document = z.infer<typeof DocumentSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const DocumentCreateInputSchema = z.object({
  ...documentCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un document. */
export type DocumentCreateInput = z.infer<typeof DocumentCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const DocumentUpdateInputSchema = z
  .object({ ...documentCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un document. */
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInputSchema>;
